import {
  FG_TOGGLE_BTN,
  FIELD_GOAL_RESULT_INPUTS,
  FUMBLE_INPUTS,
  KICKOFF_INPUTS,
  KICKOFF_YARDLINE,
  PAT_INPUTS,
  PRIMARY_FLOW_INPUTS,
  REF_EVENT_INPUTS,
  REGULAR_PLAY_INPUTS,
  TEventTypes,
  TPlayDesigns,
  TPlayResultTypes,
  TQuarters,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import {
  IConfirmationInput,
  IMachineContext,
  ITwoRowGenericSelectorInp,
  IYardageSelectorInput,
  TMetaFuncInput,
  TMachineGlobalEvents,
  TOffDef,
  TPlayResults,
  TPlays,
  TRefEventsActions,
  TRegularPlayEvents,
  TTeam,
  TTeamSide,
  TWorkflowType,
} from '@/types';
import { TFieldGoalEvents } from '@/types/workflow/fieldGoalMachine';
import { refEventValue } from '@/utilities';
import { sendPenaltyEvent } from '@/workflow/utils';
import { last } from 'lodash';

export const onFGSelected = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.toggles[TPlays.PENALTY_ALERT]) {
    sendPenaltyEvent(
      playFlowState,
      generalState,
      send,
      { playDesignId: TPlayDesigns.FG },
      TFieldGoalEvents.PENALTY_SELECTED,
      TRefEventsActions.BACK_TO_FG_START
    );
  }

  switch (payload.value) {
    case TPlays.FG:
      send(TFieldGoalEvents.ON_FG, {
        nextStateInput: FIELD_GOAL_RESULT_INPUTS,
      });
      break;
    case TPlays.FAKE_FG:
      send(TFieldGoalEvents.ON_FAKE, {
        ctxUpdate: {
          currentWorkflow: TWorkflowType.REGULAR_PLAY,
          currentPlay: {
            options: {
              ...context.currentPlay?.options,
              isFakePunt: true,
            },
          },
        },
        nextStateInput: REGULAR_PLAY_INPUTS(TRegularPlayEvents.BACK_TO_FAKE_FIELD_GOAL),
      });
      break;
    case TPlays.FUMBLE:
      send(TFieldGoalEvents.ON_FUMBLE, {
        nextStateInput: FUMBLE_INPUTS(TFieldGoalEvents.TO_START),
      });
      break;
    case TPlays.PAT:
      send(TMachineGlobalEvents.TO_PAT_GLOBAL, {
        nextStateInput: {
          ...PAT_INPUTS,
          showBackBtn: true,
          backTo: TFieldGoalEvents.TO_START,
        },
        ctxUpdate: {
          currentWorkflow: TWorkflowType.PAT,
          currentPlay: null,
        },
      });
      break;
    case TPlays.REF:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.REF_EVENT,
          value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
        },
        generalState.currentGame.gameId
      );

      send(TFieldGoalEvents.ON_REF, {
        nextStateInput: {
          ...REF_EVENT_INPUTS,
          backTo: TRefEventsActions.BACK_FROM_REF,
        } as ITwoRowGenericSelectorInp,
      });
      break;
    default:
  }
};

export const onFGResultSelected = ({
  send,
  payload,
  generalState,
  playFlowState,
  context,
}: TMetaFuncInput) => {
  switch (payload.value) {
    case TPlayResults.FG_RETURN:
      if (payload.toggles[TPlays.PENALTY_ALERT]) {
        sendPenaltyEvent(
          playFlowState,
          generalState,
          send,
          { playDesignId: TPlayDesigns.FG },
          TFieldGoalEvents.PENALTY_SELECTED,
          TRefEventsActions.BACK_TO_FG_RESULT
        );
      } else {
        send(TFieldGoalEvents.ON_RETURN, {
          nextStateInput: {
            buttons: YARDAGE_SELECTOR_BUTTONS(),
            sliderLabels: {
              left: generalState.teams[playFlowState.teams.left].abbreviation,
              right: generalState.teams[playFlowState.teams.right].abbreviation,
            },
          } as IYardageSelectorInput,
          ctxUpdate: {
            currentPlay: {
              playDesignId: TPlayDesigns.FG,
              resultType: [TPlayResultTypes.FG_MISS],
              options: {
                ...context.currentPlay?.options,
                isFGReturned: true,
              },
            },
          },
        });
      }
      break;
    case TPlayResults.FG_MISS:
    case TPlayResults.FG_MAKE: {
      let postPlaySide;
      let resultType;
      let postPlayYardLine;
      if (payload.value === TPlayResults.FG_MAKE) {
        postPlaySide =
          playFlowState.teams.left === playFlowState.presentStateBasedOnCurrentMode.activeTeam
            ? TTeamSide.LEFT
            : TTeamSide.RIGHT;
        resultType = TPlayResultTypes.FG;
        postPlayYardLine = KICKOFF_YARDLINE;
      } else {
        postPlaySide = playFlowState.presentStateBasedOnCurrentMode.yardLine.side;
        postPlayYardLine = playFlowState.presentStateBasedOnCurrentMode.yardLine.number;
        resultType = TPlayResultTypes.FG_MISS;
      }

      if (payload.toggles[TPlays.PENALTY_ALERT]) {
        sendPenaltyEvent(
          playFlowState,
          generalState,
          send,
          {
            playDesignId: TPlayDesigns.FG,
            resultType: [resultType],
          },
          TFieldGoalEvents.PENALTY_SELECTED,
          TRefEventsActions.BACK_TO_FG_RESULT
        );
      } else {
        send(TFieldGoalEvents.ON_FG, {
          nextStateInput: {
            toggleButton: FG_TOGGLE_BTN(resultType === TPlayResultTypes.FG),
            backTo: TFieldGoalEvents.TO_RESULT,
          } as IConfirmationInput,
          ctxUpdate: {
            currentPlay: {
              playDesignId: TPlayDesigns.FG,
              resultType: [resultType],
              postPlaySide,
              postPlayYardLine,
            },
          } as IMachineContext,
        });
      }
      break;
    }
    case TPlays.FUMBLE:
      send(TFieldGoalEvents.ON_FUMBLE, {
        nextStateInput: FUMBLE_INPUTS(TFieldGoalEvents.TO_RESULT),
        ctxUpdate: {
          currentPlay: {
            playDesignId: TPlayDesigns.FG,
            resultType: [TPlayResultTypes.FG_MISS],
          },
        },
      });
      break;
    case TPlays.REF:
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.REF_EVENT,
          value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
        },
        generalState.currentGame.gameId
      );

      send(TFieldGoalEvents.ON_REF, {
        nextStateInput: {
          ...REF_EVENT_INPUTS,
          backTo: TRefEventsActions.BACK_FROM_REF,
        } as ITwoRowGenericSelectorInp,
        ctxUpdate: {
          currentPlay: {
            playDesignId: TPlayDesigns.FG,
          },
        },
      });
      break;
    default:
  }
};

export const onFGConfirmedSelected = ({ send, payload, playFlowState, context }: TMetaFuncInput) => {
  if (last(context.currentPlay.resultType) === TPlayResultTypes.TD) {
    return send(TFieldGoalEvents.CONFIRM_TD, {
      ctxUpdate: {
        currentWorkflow: TWorkflowType.PAT,
        currentPlay: {
          ...context.currentPlay,
          resultType: [TPlayResultTypes.FG_MISS_TD],
          options: {
            ...context.currentPlay?.options,
            isKickoffOrPunt: true,
            isScore: true,
          },
        },
      } as IMachineContext,
      nextStateInput: PAT_INPUTS,
    });
  }

  if (payload.toggles[TPlayResults.FG_MAKE] && playFlowState.currentQuarter <= TQuarters.Q4) {
    return send(TFieldGoalEvents.CONFIRM_MAKE, {
      ctxUpdate: {
        currentWorkflow: TWorkflowType.KICKOFF,
        currentPlay: {
          ...context.currentPlay,
          resultType: [TPlayResultTypes.FG],
          options: {
            ...context.currentPlay?.options,
            isPatOrFgMake: true,
          },
        },
      } as IMachineContext,
      nextStateInput: KICKOFF_INPUTS(),
    });
  }
  if (payload.toggles[TPlayResults.FG_MAKE]) {
    return send(TFieldGoalEvents.FG_MAKE_OVERTIME, {
      ctxUpdate: {
        currentWorkflow: null,
        currentPlay: {
          ...context.currentPlay,
          resultType: [TPlayResultTypes.FG],
          options: {
            ...context.currentPlay?.options,
            isPatOrFgMake: true,
          },
        },
      } as IMachineContext,
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  }

  let resultType = TPlayResultTypes.FG_MISS;
  if (context.currentPlay.penalty === TOffDef.DEFENSE) {
    resultType =
      playFlowState.currentTeamRoles.defense === TTeam.HOME
        ? TPlayResultTypes.PENALTY_HOME
        : TPlayResultTypes.PENALTY_AWAY;
  } else if (context.currentPlay.penalty === TOffDef.OFFENSE) {
    resultType =
      playFlowState.currentTeamRoles.offense === TTeam.HOME
        ? TPlayResultTypes.PENALTY_HOME
        : TPlayResultTypes.PENALTY_AWAY;
  } else if (context.currentPlay.penalty === TOffDef.BOTH) {
    resultType = TPlayResultTypes.PENALTY_BOTH;
  }

  return send(TFieldGoalEvents.CONFIRM_MISS, {
    ctxUpdate: {
      currentWorkflow: null,
      currentPlay: {
        ...context.currentPlay,
        resultType: [resultType],
        options: {
          ...context.currentPlay?.options,
          isKickoffOrPunt: !context.currentPlay.penalty,
          isPatOrFgMake: false,
        },
      },
    } as IMachineContext,
    nextStateInput: PRIMARY_FLOW_INPUTS,
  });
};
